/**
 * The canonical reserved-slug list.
 *
 * A tenant subdomain is `<slug>.<baseDomain>`. Any label we use — or might
 * plausibly want — for our own surfaces must be unavailable to tenants, or a
 * customer can claim a name that shadows one of our hosts. Reserving a name is
 * free until someone claims it; un-claiming one afterwards is not.
 *
 * # Why this lives in its own package
 *
 * Before this, the list existed TWICE — once in `services/api` and once in
 * `apps/admin` — held in sync by nothing but discipline. They happened to agree
 * at 25 entries each, but two independently-edited copies of a security-relevant
 * allowlist is a drift waiting to happen, and the drift is silent: the API would
 * refuse a slug the admin UI had already accepted, or worse, the reverse. At
 * 100+ entries that stops being hypothetical. Both modules now re-export from
 * here, so there is exactly one list to edit and one to review.
 *
 * # Editing this list
 *
 * Additions are cheap and need no ceremony — add the name to the right category
 * and the count test moves with it. **Removals are owner-only**: taking a name
 * off this list makes it claimable, which is a decision about what we are
 * willing to let a customer occupy, not a refactor.
 *
 * The categories are deliberate. Brainstorming produces a list that looks
 * plausible and misses whole classes; deriving from categories is what makes the
 * coverage systematic and lets the next person see where a new name belongs.
 */

/** Our own product surfaces, current and plausible. */
const OWN_SURFACES = [
  "www",
  "api",
  "app",
  "apps",
  "gtcc",
  "gtcc-staging",
  "staging",
  "admin",
  "administrator",
  "administration",
  "platform",
  "cockpit",
  "console",
  "control",
  "dashboard",
  "portal",
  "panel",
  "manage",
  "management",
  "backoffice",
] as const;

/** Infrastructure and technical hostnames. */
const INFRA = [
  "mail",
  "smtp",
  "imap",
  "pop",
  "mx",
  "webmail",
  "mailer",
  "noreply",
  "no-reply",
  "postmaster",
  "dns",
  "ns",
  "ns1",
  "ns2",
  "cdn",
  "static",
  "assets",
  "files",
  "storage",
  "backup",
  "backups",
  "db",
  "database",
  "sql",
  "redis",
  "cache",
  "queue",
  "worker",
  "cron",
  "jobs",
  "gateway",
  "ingest",
  "relay",
  "proxy",
  "edge",
  "lb",
  "vpn",
  "wireguard",
  "bastion",
  "ssh",
  "ftp",
  "sftp",
  "git",
  "ci",
  "cd",
  "build",
  "deploy",
  "registry",
  "docker",
  "k8s",
  "monitor",
  "monitoring",
  "metrics",
  "prometheus",
  "grafana",
  "alert",
  "alerts",
  "alertmanager",
  "logs",
  "logging",
  "status",
  "health",
  "uptime",
  "debug",
  "trace",
  "test",
  "testing",
  "dev",
  "developer",
  "development",
  "demo",
  "sandbox",
  "preview",
  "beta",
  "alpha",
  "canary",
  "local",
  "localhost",
  "internal",
  "intern",
  "private",
  "public",
] as const;

/** Identity, authentication and security. */
const IDENTITY = [
  "auth",
  "login",
  "logout",
  "signin",
  "signup",
  "register",
  "callback",
  "sso",
  "oauth",
  "oidc",
  "keycloak",
  "identity",
  "id",
  "account",
  "accounts",
  "user",
  "users",
  "member",
  "members",
  "session",
  "token",
  "password",
  "secure",
  "security",
  "abuse",
  "phishing",
  "root",
  "sysadmin",
  "superuser",
  "owner",
  "master",
  "system",
] as const;

/** Brand, business and public-facing names. */
const BUSINESS = [
  "genius",
  "geniustracker",
  "genius-tracker",
  "gt",
  "geletec",
  "support",
  "help",
  "helpdesk",
  "service",
  "contact",
  "info",
  "sales",
  "shop",
  "store",
  "billing",
  "invoice",
  "payment",
  "pay",
  "legal",
  "privacy",
  "terms",
  "imprint",
  "impressum",
  "news",
  "blog",
  "docs",
  "documentation",
  "wiki",
  "faq",
  "download",
  "downloads",
  "update",
  "updates",
  "mobile",
  "ios",
  "android",
] as const;

/**
 * The categories, exported so the test can assert each one is represented and a
 * future reader can see where a new name belongs.
 */
export const RESERVED_SLUG_CATEGORIES = {
  ownSurfaces: OWN_SURFACES,
  infra: INFRA,
  identity: IDENTITY,
  business: BUSINESS,
} as const satisfies Record<string, readonly string[]>;

/**
 * Every reserved label, lowercased. Membership is tested against the cleaned
 * (trimmed + lowercased) candidate, so matching is case-insensitive by
 * construction rather than by a separate code path.
 */
export const RESERVED_TENANT_SLUGS: ReadonlySet<string> = new Set<string>([
  ...OWN_SURFACES,
  ...INFRA,
  ...IDENTITY,
  ...BUSINESS,
]);

/**
 * The floor the test pins. The Owner asked for at least 100; the pin exists so a
 * refactor cannot quietly shrink the list back down, which is the failure mode
 * that would reopen every name at once without anyone noticing.
 */
export const RESERVED_TENANT_SLUGS_MIN_COUNT = 100;
